/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Tariffs And Services Card Component
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Alexander Golshtein
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.30.0
 * @since 1.4.0
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import {
    ProductCardPersonalInfoStyled,
} from '@pages/PageProducts/components/ProductCards/styled/ProductCardTariffStyled';
import { getRecurringChargeItem } from '@pages/PageProducts/components/ProductCards/utils/getRecurringChargeItem';
import { stringFormatParams } from '@utils/stringFormat';

import { icons } from '../../../../assets';
import { EllipseText, LinkBlock } from '../../../../components/common';
import { ImgStyled } from '../../../../components/common/Block/Img';
import { LayoutStyled } from '../../../../components/common/Block/Layout';
import ArrowTooltip from '../../../../components/common/Tooltip/Tooltip';
import { ICommonTranslation } from '../../../../modules/internationalization/types';
import { IPageProductsTranslation } from '../../../../modules/internationalization/types/pages/products';
import { ONBOARDING_DATA_ATTR } from '../../../../modules/onboarding/onboardingConstants';
import { EOnboardingComponentsId } from '../../../../modules/onboarding/types/onboardingTypes';
import { formatPrice, formatPriceWithMeasureUnit } from '../../../../utils/formatText';
import { TProductItem } from '../../tariffsAndServicesProductTypes';
import { IProductOfferings } from '../../tariffsAndServicesTypes';
import {
    ProductCardStyled,
    ProductsCardCostStyled,
    ProductsCardDescrStyled,
    ProductsCardMoreStyled,
} from '../styled/ProductsContentStyled';

interface ITariffsAndServicesCardProps {
    isMegaPowers?: boolean;
    isConnected?: boolean;
    translation: IPageProductsTranslation;
    translationCommon: ICommonTranslation;
}

export type TProductsCard = IProductOfferings & ITariffsAndServicesCardProps & TProductItem;

const ProductCardService = (props: TProductsCard) => {
    const {
        connected,
        limitConnections,
        isMegaPowers = false,
        isConnected,
        available,
        productOfferingOneTimeCharges,
        productOfferingRecurringCharges,
        translation,
        translationCommon,
    } = props;

    const [oneTimeCharge, setOneTimeCharge] = useState(props.translation.cards.freeLabel);
    const [recurringCharge, setRecurringCharge] = useState(props.translation.cards.freeLabel);

    const recurringChargeItem = useMemo(
        () => getRecurringChargeItem(productOfferingRecurringCharges),
        [productOfferingRecurringCharges],
    );

    useEffect(() => {
        let price = 0;
        if (productOfferingOneTimeCharges?.length > 1) {
            price = productOfferingOneTimeCharges.reduce((acc, cur) => acc + cur.price, 0);
        }
        if (productOfferingOneTimeCharges?.length === 1) {
            price = productOfferingOneTimeCharges[0].price;
        }
        if (price) {
            setOneTimeCharge(formatPrice(price, translationCommon.currency));
        } else {
            setOneTimeCharge(props.translation.cards.freeLabel);
        }
    }, [productOfferingOneTimeCharges, props.translation, translationCommon]);

    useEffect(() => {
        if (recurringChargeItem && recurringChargeItem.price) {
            setRecurringCharge(
                formatPriceWithMeasureUnit(
                    recurringChargeItem.price,
                    translationCommon.currency,
                    recurringChargeItem.period.periodLength,
                    recurringChargeItem.period.measureUnit.name,
                ),
            );
        } else {
            setRecurringCharge(props.translation.cards.freeLabel);
        }
    }, [translationCommon, props.translation, recurringChargeItem]);

    const isRecurringTimeChargeShowTooltip = productOfferingRecurringCharges?.length > 1 && !isMegaPowers;

    const isMegaPowersShowTooltip =
        productOfferingRecurringCharges?.length > 0
        && isMegaPowers
        && !!recurringChargeItem?.price;

    const isOneTimeChargeShowTooltip = productOfferingOneTimeCharges?.length > 1;

    const getCosts = useCallback(() => {
        if (isConnected) {
            return null;
        }
        if (!available) {
            return <ProductsCardCostStyled>{translation.cards.notAvailableLabel}</ProductsCardCostStyled>;
        }
        return (
            <Fragment>
                {productOfferingOneTimeCharges && (
                    <ProductsCardCostStyled>
                        {translation.cards.activationLabel} <span>{oneTimeCharge}</span>
                        {isOneTimeChargeShowTooltip && (
                            <LayoutStyled className="ml-1">
                                <ArrowTooltip
                                    message={translation.cards.severalOneTimeChargesTooltip.text}
                                    title={translation.cards.severalOneTimeChargesTooltip.title}
                                >
                                    <ImgStyled className="small" src={icons.infoBlueIcon} />
                                </ArrowTooltip>
                            </LayoutStyled>
                        )}
                    </ProductsCardCostStyled>
                )}
                {productOfferingRecurringCharges && (
                    <ProductsCardCostStyled>
                        {translation.cards.feeLabel} <span>{recurringCharge}</span>
                        {isRecurringTimeChargeShowTooltip && (
                            <LayoutStyled className="ml-1">
                                <ArrowTooltip
                                    message={translation.cards.severalRecurringChargesTooltip.text}
                                    title={translation.cards.severalRecurringChargesTooltip.title}
                                >
                                    <ImgStyled className="small" src={icons.infoBlueIcon} />
                                </ArrowTooltip>
                            </LayoutStyled>
                        )}
                        {isMegaPowersShowTooltip && (
                            <LayoutStyled className="ml-1">
                                <ArrowTooltip title={translation.cards.megaPowersTooltip}>
                                    <ImgStyled className="small" src={icons.infoSuccessSmallIcon} />
                                </ArrowTooltip>
                            </LayoutStyled>
                        )}
                    </ProductsCardCostStyled>
                )}
            </Fragment>
        );
    }, [
        available,
        isMegaPowersShowTooltip,
        isConnected,
        isOneTimeChargeShowTooltip,
        isRecurringTimeChargeShowTooltip,
        oneTimeCharge,
        productOfferingOneTimeCharges,
        productOfferingRecurringCharges,
        recurringCharge,
        translation,
    ]);
    const limitConnectionsTitle = stringFormatParams(translation.limitConnections, {
        limitConnections: limitConnections,
        connected: connected,
    });

    return (
        <ProductCardStyled style={{ height: props.isConnected ? '190px' : '248px' }}>
            <ProductCardPersonalInfoStyled>
                <h5>
                    <EllipseText lines={2} title={props.title}>
                        {props.title}
                    </EllipseText>
                </h5>
                {!!limitConnections && (<ArrowTooltip limit-connections-toolip='limitConnectionsTooltip' title={limitConnectionsTitle}>
                    <ImgStyled className="small" src={icons.infoSuccessSmallIcon} width={'20px'}/>
                </ArrowTooltip>)}
            </ProductCardPersonalInfoStyled>

            {getCosts()}

            <ProductsCardDescrStyled>
                <EllipseText lines={4} title={props.description}>
                    {props.description}
                </EllipseText>
            </ProductsCardDescrStyled>
            {props.mooreDetailsLink && !props.isConnected && (
                <ProductsCardMoreStyled>
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        {...{ [ONBOARDING_DATA_ATTR]: EOnboardingComponentsId.productCardMooreDetailsLink }}
                    >
                        <LinkBlock isExternalUrl={true} target="_blank" url={props.mooreDetailsLink}>
                            {props.translation.cards.moreInfoLabel}
                        </LinkBlock>
                    </span>
                </ProductsCardMoreStyled>
            )}
        </ProductCardStyled>
    );
};

export default ProductCardService;
